import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Fade } from '@mui/material';
import axios from '@/_utils/axios';

type PdfPReviewProps = {
  source: string;
};

const PdfPReview = ({ source }: PdfPReviewProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const prevBlobUrl = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setLoadError(null);
    setBlobUrl(null);

    const loadPdfBlob = async () => {
      try {
        const isAbsoluteUrl = /^https?:\/\//i.test(source);

        let bytes: ArrayBuffer;
        if (isAbsoluteUrl) {
          // Signed S3 links require no auth headers — use native fetch.
          const response = await fetch(source, {
            signal: controller.signal,
            credentials: 'omit'
          });
          if (!response.ok) throw new Error('Failed to fetch PDF');
          bytes = await response.arrayBuffer();
        } else {
          const response = await axios.get(source, {
            responseType: 'arraybuffer',
            signal: controller.signal
          });
          bytes = response?.data;
        }

        if (!bytes || bytes.byteLength < 5) throw new Error('Empty PDF response');

        const header = new TextDecoder('ascii').decode(new Uint8Array(bytes.slice(0, 5)));
        if (header !== '%PDF-') throw new Error('Response is not a PDF document');

        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })) + '#pagemode=none';
        prevBlobUrl.current = url;
        setBlobUrl(url);
      } catch {
        setLoadError('Unable to display PDF preview. The file link may be expired or blocked.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPdfBlob();

    return () => {
      controller.abort();
      if (prevBlobUrl.current) {
        URL.revokeObjectURL(prevBlobUrl.current);
        prevBlobUrl.current = null;
      }
    };
  }, [source]);

  return (
    <FadeContainer in timeout={600}>
      <PDFPreview>
        <PDFContainer>
          {isLoading && <StatusLabel>Loading preview...</StatusLabel>}
          {loadError ? (
            <ErrorLabel>{loadError}</ErrorLabel>
          ) : blobUrl ? (
            <PDFFrame src={blobUrl} title="PDF preview" />
          ) : null}
        </PDFContainer>
      </PDFPreview>
    </FadeContainer>
  );
};

export default PdfPReview;

const FadeContainer = styled(Fade)`
  width: 100%;
`;

const PDFPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const PDFContainer = styled.div`
  height: 75vh;
  width: 100%;
  position: relative;
`;

const PDFFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ErrorLabel = styled.p`
  margin: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.palette.error.main};
`;

const StatusLabel = styled.p`
  margin: ${({ theme }) => theme.spacing(2)};
  color: rgba(229, 236, 248, 0.84);
`;